import { ArrowDownIcon, ArrowUpIcon, CogIcon, EyeIcon, EyeSlashIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { editableOptionFields, IVisibleProp, loanOptionsConstants } from 'config'
import { convertNullToBlank } from 'pages/Resources/GuidelineOverlay/logic'
import { useMemo, useState } from 'react'
import { Input2, Tooltip } from 'stories/components'
import { ISelectOption } from 'stories/components/SelectOptionInputs/types'
import { toCamelCase } from 'utils'

const isPlaceholderProductTypeKey = (key: string) => /^CustomProductType\d*$/.test(key)

const productTypeKeyFromName = (name: string, keys: string[], currentKey: string) => {
  const base = toCamelCase(name.trim())
  if (!base) return currentKey
  const taken = new Set(keys.filter((item) => item !== currentKey))
  let key = base
  let suffix = 2
  while (taken.has(key)) {
    key = `${base}${suffix}`
    suffix += 1
  }
  return key
}

const buildAllOptions = (fieldKey: string, storedOptions: ISelectOption[] | undefined): ISelectOption[] => {
  const defaults = (loanOptionsConstants as any)[fieldKey]
  if (!defaults) return []

  const defaultEntries: Array<{ key: string; title: string }> = Array.isArray(defaults)
    ? (defaults as string[]).map((key) => ({ key, title: key }))
    : Object.entries(defaults as Record<string, string>).map(([key, title]) => ({ key, title }))

  if (!storedOptions?.length) {
    return defaultEntries.map(({ key, title }) => ({ key, title, visible: true, isCustom: false }))
  }

  const defaultMap = new Map(defaultEntries.map(({ key, title }) => [key, title]))
  const storedKeys = new Set<string>()

  const result = storedOptions.map((opt) => {
    storedKeys.add(opt.key)
    const defaultTitle = defaultMap.get(opt.key)
    if (defaultTitle) return { ...opt, title: opt.title || defaultTitle }
    return { ...opt, title: opt.title || opt.key, isCustom: true }
  })

  defaultEntries.forEach(({ key, title }) => {
    if (!storedKeys.has(key)) result.push({ key, title, visible: true, isCustom: false })
  })

  return result
}

export const SelectOptionsCustomization = ({
  data,
  setData,
  fieldKey,
  promptVisibleLogic,
  showAdjustments = true,
  isMasterSection = true,
}: {
  data: IVisibleProp
  setData: (data: IVisibleProp) => void
  fieldKey: string
  promptVisibleLogic: (idx: number) => Promise<any>
  showAdjustments?: boolean
  /** False on every section but Loan Structure, which owns the list these fields are read from. */
  isMasterSection?: boolean
}) => {
  const isEditableOptionField = useMemo(() => editableOptionFields.includes(fieldKey), [fieldKey])
  if (!isEditableOptionField) return null

  const storedOptions = data.options as ISelectOption[] | undefined

  const allOptions = useMemo(() => buildAllOptions(fieldKey, storedOptions), [fieldKey, storedOptions])

  const saveOptions = (opts: ISelectOption[]) => {
    const newData = cloneDeep(data)
    newData.options = opts as any
    setData(newData)
  }

  const isProductType = fieldKey === 'productType'
  const productTypeDefaults = useMemo(
    () => Object.keys((loanOptionsConstants.productType as Record<string, string>) || {}),
    [],
  )

  const onChangeTitle = (key: string, title: string) => {
    saveOptions(allOptions.map((opt) => (opt.key === key ? { ...opt, title } : opt)))
  }

  const onCommitCustomProductTitle = (key: string, title: string) => {
    saveOptions(
      allOptions.map((opt) => {
        if (opt.key !== key) return opt
        const nextKey = isPlaceholderProductTypeKey(key)
          ? productTypeKeyFromName(
              title,
              allOptions.map((item) => item.key),
              key,
            )
          : key
        return { ...opt, key: nextKey, title }
      }),
    )
  }

  const onToggleVisible = (key: string) => {
    saveOptions(allOptions.map((opt) => (opt.key === key ? { ...opt, visible: !opt.visible } : opt)))
  }

  const onMove = (index: number, direction: number) => {
    const opts = [...allOptions]
    opts.splice(index + direction, 0, opts.splice(index, 1)[0])
    saveOptions(opts)
  }

  const onRemove = (key: string) => {
    saveOptions(allOptions.filter((opt) => opt.key !== key))
  }

  const onChangeMapTo = (key: string, mapTo: string) => {
    saveOptions(allOptions.map((opt) => (opt.key === key ? { ...opt, mapTo } : opt)))
  }

  const onAddCustomProductType = () => {
    const next = cloneDeep(allOptions)
    const mapTo = productTypeDefaults[0] || 'Bridge'
    const customCount = next.filter((opt) => opt.isCustom).length + 1
    const title = `Custom Product Type ${customCount}`
    const key = productTypeKeyFromName(
      title,
      next.map((opt) => opt.key),
      '',
    )
    next.push({
      key,
      title,
      visible: true,
      isCustom: true,
      mapTo,
    })
    saveOptions(next)
  }

  const [adjustmentDrafts, setAdjustmentDrafts] = useState<Record<string, string>>({})

  const getAdjustmentValue = (key: string, field: 'LLPA' | 'LLRA', savedValue?: number) =>
    adjustmentDrafts[`${key}-${field}`] ?? convertNullToBlank(savedValue)

  const onChangeAdjustmentDraft = (key: string, field: 'LLPA' | 'LLRA', value: string) => {
    if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) return
    setAdjustmentDrafts((drafts) => ({ ...drafts, [`${key}-${field}`]: value }))
  }

  const onChangeAdjustment = (key: string, field: 'LLPA' | 'LLRA', rawValue: string) => {
    const value = rawValue === '' || isNaN(Number(rawValue)) ? NaN : Number(rawValue)
    setAdjustmentDrafts((drafts) => {
      const nextDrafts = { ...drafts }
      delete nextDrafts[`${key}-${field}`]
      return nextDrafts
    })
    saveOptions(allOptions.map((opt) => (opt.key === key ? { ...opt, [field]: value } : opt)))
  }

  const onChangeVisibleLogic = async (index: number) => {
    const result = await promptVisibleLogic(index)
    if (!result) return
    const opts = allOptions.map((opt, i) => (i === index ? { ...opt, visibleLogic: result } : opt))
    saveOptions(opts)
  }

  // Editing here would look saved and never be read, since the loan reads Loan Structure's list.
  if (!isMasterSection)
    return (
      <div className="grid gap-2">
        <p className="font-bold text-sm">Customize Options</p>
        <p className="text-xs text-gray-500">
          This field offers the same choices wherever it appears, and they are set under Loan Structure. Edit them there
          and the new names apply on this section too.
        </p>
      </div>
    )

  return (
    <div className="grid gap-2">
      <p className="font-bold text-sm">Customize Options</p>
      <p className="text-xs text-gray-500">
        The small label above each box is the value this option is stored and priced as. The box below it is the name
        your team sees on the loan, and renaming it here renames it on every screen the field appears on.
      </p>
      {isProductType && (
        <div className="flex justify-end">
          <button className="text-sm text-shade-blue hover:underline" type="button" onClick={onAddCustomProductType}>
            + Add Custom Product Type
          </button>
        </div>
      )}
      <div>
        {allOptions.map((opt, index) => {
          const isVisible = opt.visible !== false
          const Icon = isVisible ? EyeIcon : EyeSlashIcon
          const showMapToEditor = isProductType && opt.isCustom
          return (
            <div
              key={opt.isCustom ? `custom-option-${index}` : opt.key}
              className={`my-2 grid items-end gap-2 ${
                showMapToEditor ? 'grid-cols-1 md:grid-cols-6' : `grid-cols-${showAdjustments ? 5 : 3}`
              }`}
            >
              <div className={showMapToEditor ? 'md:col-span-2' : 'col-span-3'}>
                <Input2
                  name={`option-${fieldKey}-${opt.key}`}
                  title={opt.key}
                  value={opt.title}
                  className={isVisible ? 'opacity-100' : 'opacity-30'}
                  onChange={(value: string) => onChangeTitle(opt.key, value)}
                  onBlur={
                    isProductType && opt.isCustom
                      ? (value: string) => onCommitCustomProductTitle(opt.key, value)
                      : undefined
                  }
                  additionalElements={() => (
                    <div className="flex gap-2">
                      <Tooltip message="Visible / Invisible">
                        <Icon className="w-4 h-4 cursor-pointer" onClick={() => onToggleVisible(opt.key)} />
                      </Tooltip>
                      {opt.isCustom && (
                        <TrashIcon className="w-4 h-4 cursor-pointer text-red-500" onClick={() => onRemove(opt.key)} />
                      )}
                      <ArrowUpIcon
                        className={`w-4 h-4 cursor-pointer text-shade-blue ${
                          index === 0 ? 'opacity-30 pointer-events-none' : ''
                        }`}
                        onClick={() => onMove(index, -1)}
                      />
                      <ArrowDownIcon
                        className={`w-4 h-4 cursor-pointer text-shade-blue ${
                          index === allOptions.length - 1 ? 'opacity-30 pointer-events-none' : ''
                        }`}
                        onClick={() => onMove(index, 1)}
                      />
                      <Tooltip message="Visible Logic">
                        <CogIcon
                          className="w-4 h-4 cursor-pointer text-shade-blue"
                          onClick={() => onChangeVisibleLogic(index)}
                        />
                      </Tooltip>
                    </div>
                  )}
                />
              </div>
              {showAdjustments && (
                <>
                  <Input2
                    type="text"
                    title="LLPA"
                    value={getAdjustmentValue(opt.key, 'LLPA', opt?.LLPA)}
                    className={isVisible ? 'opacity-100' : 'opacity-30'}
                    onChange={(value: string) => onChangeAdjustmentDraft(opt.key, 'LLPA', value)}
                    onBlur={(value: string) => onChangeAdjustment(opt.key, 'LLPA', value)}
                  />
                  <Input2
                    type="text"
                    title="LLRA"
                    value={getAdjustmentValue(opt.key, 'LLRA', opt?.LLRA)}
                    className={isVisible ? 'opacity-100' : 'opacity-30'}
                    onChange={(value: string) => onChangeAdjustmentDraft(opt.key, 'LLRA', value)}
                    onBlur={(value: string) => onChangeAdjustment(opt.key, 'LLRA', value)}
                  />
                </>
              )}
              {showMapToEditor && (
                <div className={`group relative md:col-span-2 z-0 w-full ${isVisible ? 'opacity-100' : 'opacity-30'}`}>
                  <select
                    className="block w-full rounded-xl border border-slate-300 bg-white px-3 pb-2 pt-[27px] text-[15px] text-gray-900 transition hover:border-slate-400 focus:border-shade-blue focus:outline-none focus:ring-2 focus:ring-shade-blue/20"
                    value={opt.mapTo || productTypeDefaults[0] || ''}
                    onChange={(e) => onChangeMapTo(opt.key, e.target.value)}
                  >
                    {productTypeDefaults.map((item) => (
                      <option value={item} key={`${opt.key}-map-${item}`}>
                        {(loanOptionsConstants.productType as any)[item] || item}
                      </option>
                    ))}
                  </select>
                  <label className="pointer-events-none absolute left-3 top-2 z-10 max-w-[calc(100%-1.5rem)] truncate text-[11px] font-medium text-slate-500">
                    Clone From
                  </label>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
